import { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, X, UserCheck, UserX, Eye, Save, ChevronRight } from 'lucide-react';
import { getAvatarUrl } from '../../utils/avatar';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function ManageStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({ full_name: '', email: '', student_id_number: '', phone_no: '', grade_id: '', resource_grade_id: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [studentsRes, gradesRes] = await Promise.all([
        api.get('/admin/students'),
        api.get('/admin/grades')
      ]);
      if (studentsRes.data.success) setStudents(studentsRes.data.students || []);
      if (gradesRes.data.success) setGrades(gradesRes.data.grades || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ full_name: '', email: '', student_id_number: '', phone_no: '', grade_id: '', resource_grade_id: '', password: '' });
    setFieldErrors({});
    setEditMode(false);
    setSelectedStudent(null);
    setError('');
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = async (student) => {
    try {
      // Fetch latest student details (includes additional grades)
      const res = await api.get(`/admin/students/${student.user_id}`);
      if (res.data.success) {
        const s = res.data.data?.student || res.data.student || null;
        if (!s) {
          setError('Failed to load student details');
          return;
        }
        setFormData({
          full_name: s.full_name || '',
          email: s.email || '',
          student_id_number: s.student_id_number || '',
          phone_no: s.phone_number || s.phone_no || '',
          grade_id: s.grade_id || '',
          resource_grade_id: s.resource_grade_id || '',
          password: ''
        });
        setSelectedStudent(s);
        setEditMode(true);
        setShowModal(true);
      } else {
        setError('Failed to load student details');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student details');
    }
  };

  const openViewModal = async (student) => {
    try {
      const res = await api.get(`/admin/students/${student.user_id}`);
      if (res.data.success) {
        const s = res.data.data?.student || res.data.student || null;
        if (!s) {
          setError('Failed to load student details');
          return;
        }
        setSelectedStudent(s);
        setShowViewModal(true);
      } else {
        setError('Failed to load student details');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // Required fields validation
    const errors = {};
    if (!formData.full_name || !formData.full_name.trim()) errors.full_name = 'Required';
    if (!formData.email || !formData.email.trim()) errors.email = 'Required';
    if (!formData.student_id_number || !formData.student_id_number.trim()) errors.student_id_number = 'Required';
    if (!formData.grade_id) errors.grade_id = 'Required';

    // Validate phone number - must be 10 digits when provided
    if (formData.phone_no && !/^\d{10}$/.test(formData.phone_no.replace(/\D/g, ''))) {
      errors.phone_no = 'Phone number must be exactly 10 digits';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fill in the required fields highlighted in red');
      return;
    }

    // Prepare payload
    const payload = { ...formData };
    // ensure numeric IDs
    payload.grade_id = parseInt(payload.grade_id) || null;
    payload.resource_grade_id = payload.resource_grade_id ? parseInt(payload.resource_grade_id) : null;

    try {
      if (editMode && selectedStudent) {
        const res = await api.put(`/admin/students/${selectedStudent.user_id}`, payload);
        if (res.data.success) {
          setSuccess('Student updated successfully!');
          setShowModal(false);
          resetForm();
          loadData();
        }
      } else {
        const res = await api.post('/admin/students', payload);
        if (res.data.success) {
          setSuccess(`Student created! ID: ${res.data.student?.student_id_number} | Email: ${formData.email} | Password: ${formData.password || res.data.student?.password}`);
          setShowModal(false);
          resetForm();
          loadData();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save student');
    }
  };

  const toggleStatus = async (student) => {
    try {
      // Ensure numeric status (DB returns 0/1 or '0'/'1') and toggle explicitly
      const current = Number(student.is_active) === 1 ? 1 : 0;
      const newStatus = current === 1 ? 0 : 1;
      await api.put(`/admin/students/${student.user_id}/status`, { is_active: newStatus });
      setSuccess(`Student ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
      loadData();
    } catch (err) {
    }
  };

  const filteredStudents = students.filter(s =>
    s.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase()) ||
    s.student_id_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manage Students</h1>
          <p className="text-gray-500">Register and manage student accounts</p>
        </div>
        <button onClick={openAddModal} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
          <Plus className="w-5 h-5" /> Add Student
        </button>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Students ({filteredStudents.length})</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search students..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-64" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Grade</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredStudents.map((student) => (
                <tr key={student.user_id} className="hover:bg-indigo-50 cursor-pointer transition" onClick={() => navigate(`/admin/students/${student.user_id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
                        {student.avatar_thumb_path || student.avatar_path ? (
                          <img src={getAvatarUrl(student.avatar_thumb_path || student.avatar_path)} alt="avatar" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-indigo-600 font-medium">{student.full_name?.charAt(0)}</span>
                        )}
                      </div>
                      <span className="font-medium text-gray-800">{student.full_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{student.email}</td>
                  <td className="px-4 py-3 text-gray-600">{student.phone_no || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{student.student_id_number || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">{student.grade_name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${Number(student.is_active) === 1 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                      {Number(student.is_active) === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={(e) => { e.stopPropagation(); openEditModal(student); }} className="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition" title="Edit">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleStatus(student); }} className={`p-2 rounded-lg transition ${Number(student.is_active) === 1 ? 'hover:bg-red-50 text-red-500' : 'hover:bg-green-50 text-green-500'}`}
                          title={Number(student.is_active) === 1 ? 'Deactivate' : 'Activate'}>
                          {Number(student.is_active) === 1 ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </td> 
                </tr>
              ))}
              {filteredStudents.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">No students found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Student Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowModal(false); resetForm(); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">{editMode ? 'Edit Student' : 'Add New Student'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input type="text" required value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${fieldErrors.full_name ? 'border-red-500 focus:ring-red-500 focus:ring-2' : 'border-gray-300 focus:ring-indigo-500'}`} />
                {fieldErrors.full_name && <p className="mt-1 text-xs text-red-600">{fieldErrors.full_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${fieldErrors.email ? 'border-red-500 focus:ring-red-500 focus:ring-2' : 'border-gray-300 focus:ring-indigo-500'}`} />
                {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student ID Number <span className="text-red-500">*</span></label>
                <input type="text" value={formData.student_id_number} onChange={(e) => setFormData({...formData, student_id_number: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${fieldErrors.student_id_number ? 'border-red-500 focus:ring-red-500 focus:ring-2' : 'border-gray-300 focus:ring-indigo-500'}`} />
                {fieldErrors.student_id_number && <p className="mt-1 text-xs text-red-600">{fieldErrors.student_id_number}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input type="tel" value={formData.phone_no} onChange={(e) => setFormData({...formData, phone_no: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${fieldErrors.phone_no ? 'border-red-500 focus:ring-red-500 focus:ring-2' : 'border-gray-300 focus:ring-indigo-500'}`} />
                {fieldErrors.phone_no && <p className="mt-1 text-xs text-red-600">{fieldErrors.phone_no}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade <span className="text-red-500">*</span></label>
                <select required value={formData.grade_id} onChange={(e) => setFormData({...formData, grade_id: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${fieldErrors.grade_id ? 'border-red-500 focus:ring-red-500 focus:ring-2' : 'border-gray-300 focus:ring-indigo-500'}`}>
                  <option value="">Select Grade</option>
                  {grades.map(g => <option key={g.grade_id} value={g.grade_id}>{g.grade_name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resources Grade (optional)</label>
                <select value={formData.resource_grade_id} onChange={(e) => setFormData({...formData, resource_grade_id: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${fieldErrors.resource_grade_id ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'}`}>
                  <option value="">None (no secondary resources grade)</option>
                  {grades.map(g => <option key={g.grade_id} value={g.grade_id}>{g.grade_name}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Select one additional grade whose resources this student should be able to access (does not affect exams/attendance).</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password {editMode ? '(leave blank to keep current)' : ''}
                </label>
                <input type="text" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder={editMode ? 'Enter new password to change' : 'Auto-generated if empty'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2">
                  {editMode ? <><Save className="w-4 h-4" /> Update</> : <><Plus className="w-4 h-4" /> Create</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Student Modal */}
      {showViewModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowViewModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Student Details</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex flex-col items-center mb-6">
              <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center mb-3">
                <span className="text-indigo-600 font-bold text-2xl">{selectedStudent.full_name?.charAt(0)}</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">{selectedStudent.full_name}</h3>
              <span className={`mt-2 px-3 py-1 rounded-full text-xs font-medium ${Number(selectedStudent.is_active) === 1 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                {Number(selectedStudent.is_active) === 1 ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-500">Email</span>
                <span className="font-medium text-gray-800">{selectedStudent.email}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-500">Student ID</span>
                <span className="font-medium text-gray-800">{selectedStudent.student_id_number || '-'}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-500">Phone Number</span>
                <span className="font-medium text-gray-800">{(selectedStudent.phone_number ?? selectedStudent.phone_no) || '-'}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-500">Grade</span>
                <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">{selectedStudent.grade_name}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-500">Resources Grade</span>
                <span className="font-medium text-gray-800">{selectedStudent.resource_grade_name || (selectedStudent.additional_grades && selectedStudent.additional_grades.length > 0 ? selectedStudent.additional_grades.map(g => g.grade_name).join(', ') : 'None')}</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-gray-500">User ID</span>
                <span className="font-medium text-gray-800">#{selectedStudent.user_id}</span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowViewModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Close</button>
              <button onClick={() => { setShowViewModal(false); openEditModal(selectedStudent); }} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2">
                <Edit className="w-4 h-4" /> Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
